/**
 * PayrollDashboardPage — Bérszámfejtés áttekintő landing
 * 4 összesítő kártya (aktuális év legutóbbi futtatásából) + 12 havi trend diagram
 *
 * Route: /payroll  (PAY-11)
 */

import { QueryErrorState } from '@/components/QueryErrorState';
import { useTranslation } from '@kgc/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@kgc/ui';
import { PayrollTrendChart } from '../components/PayrollTrendChart';
import { usePayrollRuns } from '../hooks/usePayrollRuns';

export function PayrollDashboardPage() {
  const { t } = useTranslation();
  const { data: runs, isError, refetch } = usePayrollRuns({ year: new Date().getFullYear() });

  const safeRuns = runs ?? [];
  const latest = safeRuns[0];

  const formatFt = (value: number | undefined): string =>
    value !== undefined ? `${value.toLocaleString('hu-HU')} Ft` : '—';

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold">{t('payroll.dashboard.title')}</h1>

      {isError && <QueryErrorState onRetry={() => void refetch()} />}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('payroll.dashboard.latestGross')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatFt(latest?.totalGross)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('payroll.dashboard.latestNet')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatFt(latest?.totalNet)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('payroll.dashboard.employerCost')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatFt(latest?.totalEmployerCost)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('payroll.dashboard.employees')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {latest?.employeeCount !== undefined ? String(latest.employeeCount) : '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Trend chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('payroll.dashboard.trend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <PayrollTrendChart runs={safeRuns} />
        </CardContent>
      </Card>
    </div>
  );
}
